import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Mail, Phone, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetStarted = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    volume: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Company: ${formData.company}\nExpected Volume: ${formData.volume}\n\n${formData.message}`
      });
      toast({
        title: "Application Submitted!",
        description: "We'll contact you within 24 hours to get you started.",
      });
      setFormData({ name: '', email: '', phone: '', company: '', volume: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      number: "1",
      title: "Submit Application",
      description: "Fill out the form with your details and trading requirements."
    },
    {
      number: "2",
      title: "KYC Verification",
      description: "Complete our quick and secure identity verification process."
    },
    {
      number: "3",
      title: "Start Trading",
      description: "Once approved, start trading with institutional-grade liquidity."
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Get Started with Coinexus
          </h1>
          <p className="text-xl text-[#5B6B7F]">
            Join thousands of satisfied clients trading with confidence
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 px-6 bg-[#FAFBFC]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1E3A5F] mb-12">
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#FF6B35]/50 transition-all rounded-xl">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A5F]">{step.title}</h3>
                  <p className="text-[#5B6B7F]">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E] mb-4">
              Application Form
            </h2>
            <p className="text-xl text-[#5B6B7F]">
              Fill out the form below and we'll onboard you in less than a day
            </p>
          </div>
          
          <Card className="border-2 border-gray-200 shadow-2xl rounded-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Full Name *
                    </label>
                    <Input 
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Address *
                    </label>
                    <Input 
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number *
                    </label>
                    <Input 
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F]">
                      Company Name (Optional)
                    </label>
                    <Input 
                      placeholder="Your Company Ltd."
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">
                    Expected Monthly Volume
                  </label>
                  <Input 
                    placeholder="e.g., $10,000 - $50,000"
                    value={formData.volume}
                    onChange={(e) => setFormData({...formData, volume: e.target.value})}
                    className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">
                    Additional Information
                  </label>
                  <Textarea 
                    placeholder="Tell us about your trading requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="border-2 border-gray-200 focus:border-[#FF6B35] text-[#1E3A5F] min-h-32 rounded-lg"
                  />
                </div>
                
                <div className="bg-[#FAFBFC] rounded-xl p-6 border-2 border-gray-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#5B6B7F]">
                      By submitting this form, you agree to our Terms & Conditions and Privacy Policy. We'll contact you within 24 hours to complete your verification.
                    </p>
                  </div>
                </div>
                
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:from-[#F7931E] hover:to-[#FF6B35] text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                  {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default GetStarted;